import { useState, useMemo, useEffect } from "react";
import React from "react";
import Select, { components } from "react-select";

import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "../../OperazioniAutomezzi/ControlloKmOre/ControlloKmOre.css";
import "../../HrGenerale/TrendKpi/TrendKpi.css";

// ── Opzioni dropdown ──
// Ricalcano i field parameter della matrice "Dettaglio Costi - Ore" del report Power BI:
// colonne = Parametro Dimensioni Temporali, righe = Parametro Dimensioni,
// indicatori = Parametro Misure (filtrato a Costo / Costi YoY HR / Ore Totali / Ore YoY).
const colonneOptions = [
    { value: "anno", label: "Anno" },
    { value: "trimestre", label: "Trimestre" },
    { value: "mese", label: "Mese" },
];

// reporting.costi_personale espone solo le chiavi settore e commessa:
// il fatto non ha la chiave attivita, quindi le dimensioni Attivita Livello 1-4 del
// report Power BI non sono calcolabili su questo tracciato.
const righeOptions = [
    { value: "settore", label: "Settore" },
    { value: "commessa", label: "Commessa" },
];

const indicatoreOptions = [
    { value: "costo", label: "Costo" },
    { value: "costo_yoy", label: "Costi YoY" },
    { value: "ore", label: "Ore Totali" },
    { value: "ore_yoy", label: "Ore YoY" },
];

// ── Formatters ──
// Sotto 1Mln formatta in K (interi). Da 1Mln in su passa a "X,X Mln €" (1 decimale).
const fmtEuroK = (v) => {
    if (v == null) return "";
    if (v === 0) return "0K €";
    if (Math.abs(v) >= 1_000_000) {
        return `${(v / 1_000_000).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}Mln €`;
    }
    const k = v / 1000;
    return `${Math.round(k).toLocaleString("it-IT")}K €`;
};

const fmtOre = (v) => {
    if (v == null) return "";
    return v.toLocaleString("it-IT", { maximumFractionDigits: 0 });
};

const fmtPerc = (v) => {
    if (v == null) return "";
    return `${v.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

const nomiMesi = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const meseToTrimestre = { 1: 1, 2: 1, 3: 1, 4: 2, 5: 2, 6: 2, 7: 3, 8: 3, 9: 3, 10: 4, 11: 4, 12: 4 };

// Helper YoY: stesso calcolo delle misure DAX 'Costi YoY HR' e 'Ore YoY'.
const calcYoY = (curr, prev) => {
    if (curr == null || prev == null || prev === 0) return null;
    return Math.round(((curr - prev) / Math.abs(prev)) * 1000) / 10;
};

// ── React-select: mostra max 2 valori, poi badge +N ──
const MultiValueContainer = ({ children, selectProps, data, ...props }) => {
    const values = selectProps.value || [];
    const index = values.findIndex(v => v.value === data.value);
    if (index < 2) {
        return <components.MultiValue selectProps={selectProps} data={data} {...props}>{children}</components.MultiValue>;
    }
    if (index === 2 && values.length > 2) {
        return <div className="controllo-km-ore-select-more-badge">+{values.length - 2}</div>;
    }
    return null;
};

const FullscreenIcon = ({ active }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {active ? (
            <>
                <polyline points="4 14 10 14 10 20"></polyline>
                <polyline points="20 10 14 10 14 4"></polyline>
                <line x1="14" y1="10" x2="21" y2="3"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
            </>
        ) : (
            <>
                <polyline points="15 3 21 3 21 9"></polyline>
                <polyline points="9 21 3 21 3 15"></polyline>
                <line x1="21" y1="3" x2="14" y2="10"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
            </>
        )}
    </svg>
);

// ── Helper aggregazione ──
// Costruisce dict periodo→{costo, ore, costo_yoy, ore_yoy}.
// rowsY   : righe periodo corrente (anno, mese, costo, ore)
// rowsYoY : righe periodo Y-1; vengono "shiftate" di +1 anno in modo che la chiave del
//           bucket coincida col periodo Y a cui sono confrontate.
const buildPeriodi = (rowsY, rowsYoY) => {
    const buckY = {};
    const buckYoY = {};

    const accumula = (target, key, costo, ore) => {
        if (!target[key]) target[key] = { costo: 0, ore: 0 };
        target[key].costo += costo;
        target[key].ore += ore;
    };

    const alimenta = (rows, target, shiftAnno) => {
        rows.forEach(r => {
            const costo = Number(r.costo) || 0;
            const ore = Number(r.ore) || 0;
            const anno = r.anno + shiftAnno;
            const mKey = `${anno}-${String(r.mese).padStart(2, "0")}`;
            const tKey = `${anno}-trim${meseToTrimestre[r.mese]}`;
            const yKey = `${anno}`;
            [mKey, tKey, yKey, "totale"].forEach(k => accumula(target, k, costo, ore));
        });
    };

    alimenta(rowsY, buckY, 0);
    alimenta(rowsYoY, buckYoY, 1);

    const periodi = {};
    const allKeys = new Set([...Object.keys(buckY), ...Object.keys(buckYoY)]);
    allKeys.forEach(k => {
        const y = buckY[k];
        const y1 = buckYoY[k];
        periodi[k] = {
            costo: y ? y.costo : null,
            ore: y ? y.ore : null,
            costo_yoy: calcYoY(y?.costo, y1?.costo),
            ore_yoy: calcYoY(y?.ore, y1?.ore),
        };
    });
    return periodi;
};

// Costruisce l'albero gerarchico delle righe in base ai campi selezionati.
// Le righe Y-1 alimentano `rowsYoY` del nodo, le Y alimentano `rows`.
const buildAlbero = (righe, righeYoY, campiGerarchia) => {
    const [c1, c2] = campiGerarchia;
    const radice = { byKey: {} };

    const aggiungi = (r, target) => {
        const vals = [c1, c2].map(c => c ? (r[c] ?? "Non assegnato") : null);
        let nodo = radice;
        for (let i = 0; i < vals.length; i++) {
            const v = vals[i];
            if (v == null) break;
            if (!nodo.byKey[v]) {
                nodo.byKey[v] = { label: v, rows: [], rowsYoY: [], byKey: {} };
            }
            nodo = nodo.byKey[v];
            nodo[target].push(r);
        }
    };
    righe.forEach(r => aggiungi(r, "rows"));
    righeYoY.forEach(r => aggiungi(r, "rowsYoY"));

    const materializza = (nodo, level, pathKey) => {
        return Object.keys(nodo.byKey).sort().map(k => {
            const n = nodo.byKey[k];
            const id = `${pathKey}/${k}`;
            return {
                id, label: n.label, level,
                periodi: buildPeriodi(n.rows, n.rowsYoY),
                children: materializza(n, level + 1, id),
            };
        });
    };

    return materializza(radice, 0, "");
};

// Estrae anni e mesi presenti nelle righe Y (i periodi mostrati derivano solo da Y, non da Y-1).
const estraiPeriodiDisponibili = (righe) => {
    const anniSet = new Set();
    const mesiPerAnno = {};
    righe.forEach(r => {
        anniSet.add(r.anno);
        if (!mesiPerAnno[r.anno]) mesiPerAnno[r.anno] = new Set();
        mesiPerAnno[r.anno].add(r.mese);
    });
    return {
        anni: [...anniSet].sort(),
        mesiPerAnno: Object.fromEntries(
            Object.entries(mesiPerAnno).map(([a, s]) => [a, [...s].sort((x, y) => x - y)])
        ),
    };
};

// Presentazionale: dataset (rows/rowsYoY) e stato loading arrivano dal contenitore
// HrCosti, che condivide la stessa fetch con il grafico Generale (l'errore lo
// mostra il contenitore a livello pagina).
const DettaglioCostiOre = ({ rows, rowsYoY }) => {
    const righe = useMemo(() => rows || [], [rows]);
    const righeYoY = useMemo(() => rowsYoY || [], [rowsYoY]);

    const [isFullscreenTable, setIsFullscreenTable] = useState(false);

    const [selectedColonne, setSelectedColonne] = useState([colonneOptions.find(o => o.value === "anno")]);
    const [selectedRighe, setSelectedRighe] = useState([righeOptions.find(o => o.value === "settore")]);
    const [selectedIndicatore, setSelectedIndicatore] = useState([...indicatoreOptions]);
    const [expandedRows, setExpandedRows] = useState(new Set());

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape") setIsFullscreenTable(false);
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, []);

    const albero = useMemo(
        () => buildAlbero(righe, righeYoY, selectedRighe.map(r => r.value)),
        [righe, righeYoY, selectedRighe]
    );

    const { anni, mesiPerAnno } = useMemo(() => estraiPeriodiDisponibili(righe), [righe]);
    const totaliGlobali = useMemo(() => buildPeriodi(righe, righeYoY), [righe, righeYoY]);

    const haAnnoSelezionato = selectedColonne.some(o => o.value === "anno");
    const haTrimestreSelezionato = selectedColonne.some(o => o.value === "trimestre");
    const haMeseSelezionato = selectedColonne.some(o => o.value === "mese");

    const isMeseValido = (anno, mese) => (mesiPerAnno[anno] || []).includes(mese);
    const isTrimestreValido = (anno, trim) => (mesiPerAnno[anno] || []).some(m => meseToTrimestre[m] === trim);

    const periodiDaMostrare = useMemo(() => {
        if (selectedColonne.length === 0) {
            return [...anni.map(a => ({ chiave: `${a}`, etichetta: `${a}`, anno: a })), { chiave: "totale", etichetta: "Totale", anno: "totale" }];
        }
        const periodi = [];
        anni.forEach(anno => {
            if (haMeseSelezionato) {
                for (let m = 1; m <= 12; m++) {
                    if (isMeseValido(anno, m)) {
                        periodi.push({ chiave: `${anno}-${String(m).padStart(2, "0")}`, etichetta: nomiMesi[m - 1], anno });
                    }
                }
            } else if (haTrimestreSelezionato) {
                for (let t = 1; t <= 4; t++) {
                    if (isTrimestreValido(anno, t)) {
                        periodi.push({ chiave: `${anno}-trim${t}`, etichetta: `T${t}`, anno });
                    }
                }
            } else if (haAnnoSelezionato) {
                periodi.push({ chiave: `${anno}`, etichetta: `${anno}`, anno });
            }
        });
        periodi.push({ chiave: "totale", etichetta: "Totale", anno: "totale" });
        return periodi;
    }, [selectedColonne, anni, mesiPerAnno]); // eslint-disable-line react-hooks/exhaustive-deps

    const isColonnaVisibile = (chiaveInd) => selectedIndicatore.length === 0 ? true : selectedIndicatore.some(o => o.value === chiaveInd);

    const colonnePerPeriodo = useMemo(() => {
        const c = indicatoreOptions.filter(o => isColonnaVisibile(o.value)).length;
        return c || 1;
    }, [selectedIndicatore]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleRow = (id) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // ── Render metriche per un periodo ──
    const renderMetriche = (periodo, key, bold = false) => {
        const cells = [];
        const wrap = (v) => bold ? <strong>{v}</strong> : v;
        const clsYoY = (v) => v == null ? "" : v < 0 ? "value-negative" : "value-positive";

        if (isColonnaVisibile("costo")) {
            cells.push(<td key={`${key}-costo`}>{wrap(periodo?.costo != null ? fmtEuroK(periodo.costo) : "")}</td>);
        }
        if (isColonnaVisibile("costo_yoy")) {
            cells.push(<td key={`${key}-costo-yoy`} className={clsYoY(periodo?.costo_yoy)}>{wrap(periodo?.costo_yoy != null ? fmtPerc(periodo.costo_yoy) : "")}</td>);
        }
        if (isColonnaVisibile("ore")) {
            cells.push(<td key={`${key}-ore`}>{wrap(periodo?.ore != null ? fmtOre(periodo.ore) : "")}</td>);
        }
        if (isColonnaVisibile("ore_yoy")) {
            cells.push(<td key={`${key}-ore-yoy`} className={clsYoY(periodo?.ore_yoy)}>{wrap(periodo?.ore_yoy != null ? fmtPerc(periodo.ore_yoy) : "")}</td>);
        }
        return cells;
    };

    // ── Render righe (ricorsivo) ──
    const renderNodi = (nodi) => {
        const output = [];
        nodi.forEach(nodo => {
            const haFigli = nodo.children && nodo.children.length > 0;
            const isExpanded = expandedRows.has(nodo.id);
            const icona = haFigli ? (isExpanded ? "▼" : "▶") : "•";
            const classLevel = `tr-level-${nodo.level + 1}`;
            const classCell = `td-settore td-indent-${nodo.level}${haFigli ? " td-expandable" : ""}`;

            output.push(
                <tr key={nodo.id} className={classLevel}>
                    <td className={classCell} onClick={haFigli ? () => toggleRow(nodo.id) : undefined}>
                        <span className="expand-icon">{icona}</span>
                        <span className="level-value">{nodo.label}</span>
                    </td>
                    {periodiDaMostrare.map(p => (
                        <React.Fragment key={p.chiave}>
                            {renderMetriche(nodo.periodi[p.chiave], p.chiave)}
                        </React.Fragment>
                    ))}
                </tr>
            );
            if (isExpanded && haFigli) {
                output.push(...renderNodi(nodo.children));
            }
        });
        return output;
    };

    // ── Export CSV ──
    const handleExport = () => {
        const headers = [selectedRighe.map(r => r.label).join(" > ") || "Riga"];
        periodiDaMostrare.forEach(p => {
            indicatoreOptions.forEach(o => {
                if (isColonnaVisibile(o.value)) headers.push(`${p.etichetta} ${o.label}`);
            });
        });
        const lines = [headers.join(";")];

        const appendNodo = (nodo) => {
            const cells = ["  ".repeat(nodo.level) + nodo.label];
            periodiDaMostrare.forEach(p => {
                const periodo = nodo.periodi[p.chiave] || {};
                indicatoreOptions.forEach(o => {
                    if (isColonnaVisibile(o.value)) cells.push(periodo[o.value] ?? "");
                });
            });
            lines.push(cells.join(";"));
            if (nodo.children) nodo.children.forEach(appendNodo);
        };
        albero.forEach(appendNodo);

        const totRow = ["Totale"];
        periodiDaMostrare.forEach(p => {
            const t = totaliGlobali[p.chiave] || {};
            indicatoreOptions.forEach(o => {
                if (isColonnaVisibile(o.value)) totRow.push(t[o.value] ?? "");
            });
        });
        lines.push(totRow.join(";"));

        const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "dettaglio_costi_ore.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const labelRighe = selectedRighe.length > 0 ? selectedRighe[0].label : "Settore";

    return (
        <div className={`controllo-km-ore-table-section${isFullscreenTable ? " fullscreen-table" : ""}`}>
            <RotateDevice active={isFullscreenTable} />

            <div className="controllo-km-ore-table-header">
                <span className="controllo-km-ore-table-title">Dettaglio Costi - Ore</span>
                <div className="controllo-km-ore-table-header-btn">
                    <button className="controllo-km-ore-download-btn" onClick={handleExport}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Esporta
                    </button>
                    <button className="controllo-km-ore-fullscreen-btn" onClick={() => setIsFullscreenTable(!isFullscreenTable)} title={isFullscreenTable ? "Esci da tutto schermo" : "Tutto schermo"}>
                        <FullscreenIcon active={isFullscreenTable} />
                    </button>
                </div>
            </div>

            {/* Dropdown parametrici */}
            <div className="controllo-km-ore-config-row">
                <div className="controllo-km-ore-config-item">
                    <label>COLONNE</label>
                    <Select
                        classNamePrefix="controllo-km-ore-select"
                        placeholder="Selezioni multiple"
                        value={selectedColonne}
                        options={colonneOptions}
                        onChange={setSelectedColonne}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ MultiValue: MultiValueContainer }}
                    />
                </div>
                <div className="controllo-km-ore-config-item">
                    <label>RIGHE</label>
                    <Select
                        classNamePrefix="controllo-km-ore-select"
                        placeholder="Selezioni multiple"
                        value={selectedRighe}
                        options={righeOptions}
                        onChange={setSelectedRighe}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ MultiValue: MultiValueContainer }}
                    />
                </div>
                <div className="controllo-km-ore-config-item">
                    <label>INDICATORE</label>
                    <Select
                        classNamePrefix="controllo-km-ore-select"
                        placeholder="Tutti"
                        value={selectedIndicatore}
                        options={indicatoreOptions}
                        onChange={setSelectedIndicatore}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ MultiValue: MultiValueContainer }}
                    />
                </div>
            </div>

            <div className="controllo-km-ore-table-wrapper">
                <table className="controllo-km-ore-table">
                    <thead>
                        {/* Riga ANNO */}
                        {haAnnoSelezionato && (
                            <tr>
                                <th className="th-settore">Anno</th>
                                {(() => {
                                    const celle = [];
                                    anni.forEach(anno => {
                                        let colSpan = colonnePerPeriodo;
                                        if (haMeseSelezionato) {
                                            let mesiValidi = 0;
                                            for (let m = 1; m <= 12; m++) if (isMeseValido(anno, m)) mesiValidi++;
                                            colSpan = mesiValidi * colonnePerPeriodo;
                                        } else if (haTrimestreSelezionato) {
                                            let trimValidi = 0;
                                            for (let t = 1; t <= 4; t++) if (isTrimestreValido(anno, t)) trimValidi++;
                                            colSpan = trimValidi * colonnePerPeriodo;
                                        }
                                        if (colSpan > 0) celle.push(<th key={anno} colSpan={colSpan} className="th-periodo">{anno}</th>);
                                    });
                                    let rowSpanTot = 1;
                                    if (haTrimestreSelezionato) rowSpanTot++;
                                    if (haMeseSelezionato) rowSpanTot++;
                                    celle.push(<th key="tot" colSpan={colonnePerPeriodo} rowSpan={rowSpanTot} className="th-periodo th-totale-header">Totale</th>);
                                    return celle;
                                })()}
                            </tr>
                        )}
                        {/* Riga TRIMESTRE */}
                        {haTrimestreSelezionato && (
                            <tr>
                                <th className="th-settore">Trimestre</th>
                                {(() => {
                                    const celle = [];
                                    const mesiPerTrim = { 1: [1, 2, 3], 2: [4, 5, 6], 3: [7, 8, 9], 4: [10, 11, 12] };
                                    const colSpanTrim = (anno, trim) => {
                                        if (!haMeseSelezionato) return colonnePerPeriodo;
                                        return mesiPerTrim[trim].filter(m => isMeseValido(anno, m)).length * colonnePerPeriodo;
                                    };
                                    anni.forEach(anno => {
                                        for (let t = 1; t <= 4; t++) {
                                            if (isTrimestreValido(anno, t)) {
                                                celle.push(<th key={`${anno}-t${t}`} colSpan={colSpanTrim(anno, t)} className="th-periodo">Q{t} {anno}</th>);
                                            }
                                        }
                                    });
                                    if (!haAnnoSelezionato) {
                                        let rowSpanTot = 1;
                                        if (haMeseSelezionato) rowSpanTot++;
                                        celle.push(<th key="tot" colSpan={colonnePerPeriodo} rowSpan={rowSpanTot} className="th-periodo th-totale-header">Totale</th>);
                                    }
                                    return celle;
                                })()}
                            </tr>
                        )}
                        {/* Riga MESE */}
                        {haMeseSelezionato && (
                            <tr>
                                <th className="th-settore">Mese</th>
                                {(() => {
                                    const celle = [];
                                    anni.forEach(anno => {
                                        for (let m = 1; m <= 12; m++) {
                                            if (isMeseValido(anno, m)) {
                                                celle.push(<th key={`${anno}-m${m}`} colSpan={colonnePerPeriodo} className="th-periodo">{nomiMesi[m - 1]} {anno}</th>);
                                            }
                                        }
                                    });
                                    if (!haAnnoSelezionato && !haTrimestreSelezionato) {
                                        celle.push(<th key="tot" colSpan={colonnePerPeriodo} className="th-periodo th-totale-header">Totale</th>);
                                    }
                                    return celle;
                                })()}
                            </tr>
                        )}
                        {/* Riga INDICATORI (sempre visibile) */}
                        <tr>
                            <th className="th-settore">{labelRighe}</th>
                            {periodiDaMostrare.map(p => {
                                const cls = p.chiave === "totale" ? "th-metrica th-metrica-totale" : "th-metrica";
                                return (
                                    <React.Fragment key={p.chiave}>
                                        {indicatoreOptions.map(o => (
                                            isColonnaVisibile(o.value) ? <th key={o.value} className={cls}>{o.label}</th> : null
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {albero.length > 0 ? renderNodi(albero) : (
                            <tr>
                                <td className="td-settore" colSpan={100} style={{ textAlign: "center", color: "#94a3b8", fontStyle: "italic", padding: "40px" }}>
                                    {righe.length === 0 ? "Nessun dato disponibile" : "Seleziona almeno una dimensione riga per visualizzare i dati"}
                                </td>
                            </tr>
                        )}
                        {/* Riga Totale */}
                        <tr className="tr-totali">
                            <td className="td-settore"><strong>Totale</strong></td>
                            {periodiDaMostrare.map(p => (
                                <React.Fragment key={p.chiave}>
                                    {renderMetriche(totaliGlobali[p.chiave], p.chiave, true)}
                                </React.Fragment>
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DettaglioCostiOre;
